import { AdcontrolProfile, ReportTemplate } from "@/models";
import { LinkedAutomaticReport } from "@/models/Templates/ReportTemplate";
import { describeAutomaticReportLink } from "./automaticReport";
import { EMPTY } from "./emptyValue";

export const templateDisplayName = (template: ReportTemplate): string =>
  template.name || `Template #${template.id}`;

export const templateProfileName = (template: ReportTemplate): string =>
  template.profile?.name || EMPTY;

export function belongsToProfile(
  template: ReportTemplate,
  profile?: AdcontrolProfile | null
): boolean {
  return !!profile?.slug && template.profile?.slug === profile.slug;
}

export function sortByOwnership(
  templates: ReportTemplate[],
  profile?: AdcontrolProfile | null
): ReportTemplate[] {
  return [...templates].sort(
    (a, b) =>
      Number(!belongsToProfile(a, profile)) -
      Number(!belongsToProfile(b, profile))
  );
}

export function matchesTemplateQuery(
  template: ReportTemplate,
  query: string
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return templateDisplayName(template).toLowerCase().includes(needle);
}

const linked = (template: ReportTemplate): LinkedAutomaticReport[] =>
  template.automaticReports ?? [];

const linkedAutomaticReportsSentence = (template: ReportTemplate): string =>
  linked(template).map(describeAutomaticReportLink).join(", ");

export function cascadeDeleteAutomaticReportNames(
  templates: ReportTemplate[]
): string[] {
  return templates.flatMap(linked).map(describeAutomaticReportLink);
}

export function cascadeDeleteLeadIn(templates: ReportTemplate[]): string {
  if (!cascadeDeleteAutomaticReportNames(templates).length) return "";
  const pronoun = templates.length === 1 ? "lo" : "li";
  return `Verranno eliminati anche i report automatici che ${pronoun} utilizzano:`;
}

export const DELETE_IRREVERSIBLE_NOTICE = "L'operazione non è reversibile.";

export const templateDeletedMessage = (template: ReportTemplate): string =>
  `Template "${templateDisplayName(template)}" eliminato.`;

export const TEMPLATES_DELETED_MESSAGE = "Template eliminati.";

export const templateDuplicatedMessage = (
  template: ReportTemplate,
  name: string
): string =>
  `Template "${templateDisplayName(template)}" duplicato come "${name}".`;

export function editBlockedMessage(template: ReportTemplate): string | null {
  const usedByAutomaticReports = linked(template);
  if (!usedByAutomaticReports.length) return null;
  const advice =
    usedByAutomaticReports.length === 1
      ? "Modificalo dal flusso di modifica di quel report automatico."
      : "Essendo condiviso da più report automatici non è modificabile: crea un nuovo template e collegalo al report che vuoi cambiare.";
  return `Il template "${templateDisplayName(
    template
  )}" è utilizzato dai report automatici: ${linkedAutomaticReportsSentence(
    template
  )}. ${advice}`;
}
